const numberFormatterCache = new Map();
const COUNT_SELECTOR = '.count';
let counterObserver = null;

const formatNumber = (value, decimals = 0) => {
    if (!Number.isFinite(value)) {
        return '0';
    }

    const precision = Math.max(0, Math.min(10, decimals));

    if (!numberFormatterCache.has(precision)) {
        numberFormatterCache.set(precision, new Intl.NumberFormat('de-DE', {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision
        }));
    }

    const formatter = numberFormatterCache.get(precision);
    return formatter.format(value);
};

const animateCounter = (element, targetNumber, decimalPlaces, suffixText) => {
    const duration = 3000;
    let startTime = null;

    const step = (timestamp) => {
        if (!startTime) {
            startTime = timestamp;
        }

        const progress = timestamp - startTime;
        const progressRatio = Math.min(progress / duration, 1);
        const currentDisplayNumber = progressRatio * targetNumber;

        const formatted = formatNumber(
            decimalPlaces > 0
                ? parseFloat(currentDisplayNumber.toFixed(decimalPlaces))
                : Math.round(currentDisplayNumber),
            decimalPlaces
        );

        element.textContent = `${formatted}${suffixText}`;

        if (progress < duration) {
            requestAnimationFrame(step);
            return;
        }

        const finalValue = decimalPlaces > 0 ? targetNumber : Math.round(targetNumber);
        element.textContent = `${formatNumber(finalValue, decimalPlaces)}${suffixText}`;
    };

    requestAnimationFrame(step);
};

const startCounter = (element) => {
    if (!element || element.classList.contains('doneCounting')) {
        return;
    }

    element.classList.add('doneCounting');

    const textNodes = [];
    const collectTextNodes = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            textNodes.push(node);
            return;
        }

        node.childNodes.forEach(collectTextNodes);
    };

    collectTextNodes(element);

    textNodes.forEach((textNode) => {
        const fullText = textNode.nodeValue;
        const regex = /(?:(\d{1,3}(?:\.\d{3})*(?:,\d+)?)|(\d+(?:,\d+)?))([^\d]*)/g;
        let matches;
        let lastIndex = 0;
        const fragments = [];

        while ((matches = regex.exec(fullText)) !== null) {
            if (matches.index > lastIndex) {
                fragments.push(document.createTextNode(fullText.substring(lastIndex, matches.index)));
            }

            const numberStr = matches[1] || matches[2];
            const textSuffix = matches[3] || '';
            const cleanNumber = numberStr.replace(/\./g, '').replace(',', '.');
            const targetNumber = Number.parseFloat(cleanNumber);

            if (!Number.isFinite(targetNumber)) {
                fragments.push(document.createTextNode(numberStr + textSuffix));
                lastIndex = regex.lastIndex;
                continue;
            }

            const decimalPlaces = (cleanNumber.split('.')[1] || '').length;
            const numberSpan = document.createElement('span');
            numberSpan.className = 'number-counter';
            numberSpan.textContent = `${numberStr}${textSuffix}`;
            fragments.push(numberSpan);
            animateCounter(numberSpan, targetNumber, decimalPlaces, textSuffix);
            lastIndex = regex.lastIndex;
        }

        if (lastIndex < fullText.length) {
            fragments.push(document.createTextNode(fullText.substring(lastIndex)));
        }

        if (fragments.length === 0) {
            return;
        }

        const parent = textNode.parentNode;
        fragments.forEach((fragment) => parent.insertBefore(fragment, textNode));
        parent.removeChild(textNode);
    });
};

const ensureCounterObserver = () => {
    if (counterObserver) {
        return counterObserver;
    }

    if (typeof IntersectionObserver === 'undefined') {
        return null;
    }

    counterObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }
            startCounter(entry.target);
            observer.unobserve(entry.target);
        });
    }, {
        rootMargin: '0px',
        threshold: 0.1,
    });

    return counterObserver;
};

export const initNumberCounters = (root = document) => {
    if (!root) {
        return;
    }

    const observer = ensureCounterObserver();
    if (!observer) {
        return;
    }

    const elements = root.matches?.(COUNT_SELECTOR)
        ? [root]
        : Array.from(root.querySelectorAll?.(COUNT_SELECTOR) || []);

    elements
        .filter((element) => !element.classList.contains('doneCounting'))
        .forEach((element) => observer.observe(element));
};

export const destroyNumberCounters = () => {
    if (!counterObserver) {
        return;
    }
    counterObserver.disconnect();
    counterObserver = null;
};

export const initGlobalCounters = () => {
    const run = () => initNumberCounters(document);
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
        return;
    }
    run();
};
